/**
 * Small motion helpers shared by UI Engineer C's sections (Call, Cta, Footer).
 *
 * - {@link drift}: smooth pseudo-noise for organic, never-looping drift.
 * - {@link onTick}: one callback on the shared GSAP ticker (the same rAF as Lenis).
 * - {@link readRgb}: resolves a CSS colour token to RGB for canvas 2D.
 * - {@link useMaskedReveal}: the site's masked line reveal for `[data-reveal]` elements.
 *
 * @module
 */
import type { RefObject } from 'react'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { DURATION, EASE, gsap, SplitText, useGSAP } from '@/lib/gsap'
import { useAppStore } from '@/lib/store'

/** An RGB triple, 0..255 per channel. */
export type Rgb = readonly [r: number, g: number, b: number]

/**
 * Smooth 1D pseudo-noise in about −1..1: three sines at incommensurate frequencies, so the
 * motion never visibly repeats. `seed` decorrelates several drifting things.
 */
export function drift(time: number, seed = 0): number {
  return (
    Math.sin(time * 0.61 + seed * 1.7) * 0.5 +
    Math.sin(time * 1.13 + seed * 3.1 + 1.3) * 0.3 +
    Math.sin(time * 1.97 + seed * 5.3 + 4.1) * 0.2
  )
}

/**
 * Adds `tick(dt, time)` to the shared GSAP ticker and returns the remover. `dt` is in
 * seconds and clamped, so a frame after a background tab never jumps. Call it inside an
 * effect that only runs while the section is in view, so nothing ticks offscreen.
 */
export function onTick(tick: (dt: number, time: number) => void): () => void {
  const run = (time: number, deltaMs: number) => tick(Math.min(deltaMs, 64) / 1000, time)
  gsap.ticker.add(run)
  return () => gsap.ticker.remove(run)
}

/**
 * Resolves any CSS colour (e.g. `'var(--color-accent)'`) as seen from `context`, so canvas
 * code follows the design tokens and the section theme instead of hard-coding hex values.
 */
export function readRgb(context: Element, value: string): Rgb {
  const probe = document.createElement('span')
  probe.style.color = value
  probe.style.display = 'none'
  context.appendChild(probe)
  const channels =
    getComputedStyle(probe)
      .color.match(/[\d.]+/g)
      ?.map(Number) ?? []
  probe.remove()
  return [channels[0] ?? 0, channels[1] ?? 0, channels[2] ?? 0]
}

/** `rgb(r g b / a)` from a triple and an alpha. */
export function rgba([r, g, b]: Rgb, alpha: number): string {
  return `rgb(${r | 0} ${g | 0} ${b | 0} / ${alpha.toFixed(3)})`
}

/** Linear blend of two colours. */
export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

/**
 * Masked line reveal (DESIGN.md §6.3) for every `[data-reveal]` inside `scope`: lines rise
 * from `yPercent: 105`, staggered, once they cross the bottom of the viewport. Under reduced
 * motion they fade in over 0.3s. It waits for the preloader (`phase !== 'boot'`).
 * Masks get the global class `vk-line-mask`, so a module can pad them for descenders.
 */
export function useMaskedReveal(scope: RefObject<HTMLElement | null>): void {
  const introStarted = useAppStore((state) => state.phase !== 'boot')
  const reducedMotion = useReducedMotion()

  useGSAP(
    () => {
      const root = scope.current
      if (!introStarted || !root) return
      for (const element of gsap.utils.toArray<HTMLElement>('[data-reveal]', root)) {
        const scrollTrigger = { trigger: element, start: 'top 90%', once: true }
        if (reducedMotion) {
          gsap.from(element, { autoAlpha: 0, duration: 0.3, scrollTrigger })
          continue
        }
        SplitText.create(element, {
          type: 'lines',
          mask: 'lines',
          linesClass: 'vk-line',
          autoSplit: true,
          // Returning the tween lets SplitText sync it when fonts or the width re-split lines.
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 105,
              stagger: 0.08,
              duration: DURATION.cinematic,
              ease: EASE.out,
              scrollTrigger,
            }),
        })
      }
    },
    { scope, dependencies: [introStarted, reducedMotion], revertOnUpdate: true },
  )
}
